/**
 * KvkkKarti.jsx — denetmen kişi sayfasının (PersonelDetayPage) Kontroller sekmesindeki "KVKK" kartı.
 * Backlog #376 (backend #375), önceki hali #137/#138/#252/#368: üç kaynağın üç satırı → iki satır.
 *
 *  · DİJİTAL ONAY — "KVKK Gönder" kişiye SMS bağlantısı yollar; kişi bağlantıdan onaylayınca satırda "Onaylandı", onay tarihi ve
 *    üretilen KVKK belgesinin indirme bağlantısı çıkar. Onaydan sonra düğme "Yeniden Gönder" olur ve altında "Son geçerlilik: onay
 *    + 1 yıl" yazar. Yeniden göndermek yürürlükteki onayı İPTAL ETMEZ; yeni onay gelene ya da süre bitene kadar sürer. Süresi
 *    dolan onay "Süresi doldu" okunur. Kişinin telefonu yoksa düğme kapalı.
 *  · ISLAK İMZALI BELGE — "Yok | Alındı" işareti (`person_kvkk_status_id` 3 / 0; eski "Kişi Kaydı" satırı buna katlandı, 1 ve 2 giriş
 *    kuralında etkisiz olduğundan "Yok" okunur) + isteğe bağlı taranmış dosya (İncele · Onayla · Yükle) ve "Son geçerlilik": yükleme
 *    + 1 yıl, dosya yoksa Alındı tarihi + 1 yıl. Eski dosyalar kendi tarihini (2030-01-01) korur — geriye dönük süre yok.
 *
 * KVKK'nın giriş kuralındaki üç kaynağı (onaylı belge · durum 3 · dijital onay) hâlâ birbirini yazmaz; bu kart yalnızca ikisini
 * ayrı satırlarda gösterir. Bkz. util/knowledgebase/kvkk-uc-kaynak.md.
 *
 * Görünüm modeli ve (backend #375 gelene kadar) mock `services/kvkkDijitalOnayService.js`te; bu dosya yalnızca çizer. Dosya
 * yükleme / onaylama / işaretleme sayfada kalır (belge kutusu da aynı gizli dosya girdisini `#kisi-kvkk-dosya` ile açıyor).
 *
 * @param {number}   personId
 * @param {object}   person       kişi satırı (`detay.kisi`)
 * @param {object}   kvkkBelge    `person.documents.kvkk`
 * @param {boolean}  salt         birleştirilmiş (gizli) kart: salt okunur
 * @param {string}   kvkkIsi      "yukle" | "onayla" | null — sayfanın dosya işleri
 * @param {boolean}  vurgu        Kişi Listesi'nin KVKK karesinden (ya da Kart Yazdır'ın KVKK bağlantısından) gelindi: kart kırmızı çerçevelenir (#481)
 * @param {object}   sablon       kurumun KVKK İmza Belgeleri için verdiği { bosForm?, ornek? } (#503); yoksa bağlantı çıkmaz
 * @param {Function} onIncele    inceleme penceresini aç
 * @param {Function} onOnayla     dosyayı tek tıkla onayla
 * @param {Function} onYukle      `(File)` dosyayı yükle
 * @param {Function} onStatus     `(3|0)` Alındı işareti
 * @param {Function} onChanged    `(mesaj)` yazma başarılı → sayfa yeniden okur
 * @param {Function} onError      `(mesaj)` yazma başarısız
 */
import React, { useState } from "react";
import { Button, ButtonGroup, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import { CheckCircle2, FileDown, Send, ShieldCheck, Upload } from "lucide-react";
import { FILE_APPROVAL_STATUS } from "../../../services/projectService";
import {
  kvkkDurumu, kvkkGonder, kvkkOnayiSimuleEt, kvkkBelgesiUrl, kvkkMockModu, tarihCoz,
} from "../../../services/kvkkDijitalOnayService";
import { NEUTRAL_GRAY, STATUS_HEX, statusBackground } from "../../Projects/StandPills";
import { currentLocaleTag } from "../../../i18n";
import { VURGU_SINIFI } from "../kisiBaglantilari";
import BelgeSablonuBaglantilari from "../../Common/BelgeSablonuBaglantilari";

const ETIKET = { width: 120 };

const gunYaz = (v) => { const d = tarihCoz(v); return d ? d.toLocaleDateString(currentLocaleTag()) : "—"; };
const saatYaz = (v) => {
  const d = tarihCoz(v);
  return d ? d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" }) : "—";
};

/** Dolu rozet: bu temada `success` gri basıldığı için renk hex ile verilir (KkdKarti'ndeki aynı çözüm). */
function Rozet({ hex, children }) {
  return <span className="badge text-white fw-normal" style={{ background: hex }}>{children}</span>;
}

const DIJITAL_ROZET = {
  YOK: { etiket: "Yok", hex: NEUTRAL_GRAY },
  EKSIK: { etiket: "Eksik", hex: NEUTRAL_GRAY },
  GONDERILDI: { etiket: "Gönderildi", hex: STATUS_HEX.warning },
  ONAYLANDI: { etiket: "Onaylandı", hex: STATUS_HEX.success },
  SURESI_DOLDU: { etiket: "Süresi doldu", hex: STATUS_HEX.warning },
};

export default function KvkkKarti({
  personId, person, kvkkBelge, salt = false, kvkkIsi = null, vurgu = false, sablon = null, onIncele, onOnayla, onYukle, onStatus, onChanged, onError,
}) {
  const [isleniyor, setIsleniyor] = useState(null); // "gonder" | "simule" | "belge"
  // Mock yazması modül deposuna gider; sayfa yeniden okuyana kadar kartı hemen tazelemek için.
  const [, tazele] = useState(0);

  const { dijital, islak } = kvkkDurumu(personId, person, kvkkBelge);
  const rozet = DIJITAL_ROZET[dijital.durum];
  const mesgul = isleniyor != null || kvkkIsi != null;
  const dosyaSec = () => document.getElementById("kisi-kvkk-dosya")?.click();

  async function calistir(anahtar, is, basari) {
    setIsleniyor(anahtar);
    try {
      const sonuc = await is();
      tazele((n) => n + 1);
      if (basari) onChanged?.(basari);
      return sonuc;
    } catch (e) {
      onError?.(e.message || "İşlem başarısız oldu.");
      return null;
    } finally {
      setIsleniyor(null);
    }
  }

  const gonder = () => calistir("gonder", () => kvkkGonder(personId), "KVKK bağlantısı gönderildi.");
  const simuleEt = () => calistir("simule", () => kvkkOnayiSimuleEt(personId), "KVKK onayı alındı (örnek).");

  async function belgeyiAc() {
    const url = await calistir("belge", () => kvkkBelgesiUrl(personId, person));
    if (!url) return;
    window.open(url, "_blank", "noopener");
    // Sekme blob'u aldıktan sonra bırak — hemen revoke edersek sekme boş açılır.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  const dosyaDurumu = kvkkBelge?.fileId != null ? FILE_APPROVAL_STATUS[kvkkBelge.status] : null;
  const sonGecerlilikRengi = (doldu) => (doldu ? "text-warning" : "text-muted");

  return (
    <Card className={`h-100 mb-0 ${vurgu ? VURGU_SINIFI : ""}`} id="belge-karti-kvkk">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <ShieldCheck size={16} className="text-muted" />
        <span className="fw-semibold">KVKK</span>
      </CardHeader>
      <CardBody className="vstack gap-3">
        {/* ── Dijital Onay ─────────────────────────────────────────────── */}
        <div className="d-flex align-items-start gap-2 flex-wrap">
          <span className="text-muted small pt-1" style={ETIKET}>Dijital Onay</span>
          <div className="d-flex flex-column gap-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Rozet hex={rozet.hex}>{rozet.etiket}</Rozet>
              {dijital.onay && <span className="small text-muted">{gunYaz(dijital.onay.tarih)}</span>}
              {!dijital.onay && dijital.bekleyen && (
                <span className="small text-muted">{saatYaz(dijital.bekleyen.gonderimTarihi)} · onay bekleniyor</span>
              )}
              {dijital.durum === "EKSIK" && (
                <span className="small text-muted">
                  Aydınlatma {gunYaz(dijital.eksik.aydinlatmaTarihi)} · Açık rıza {gunYaz(dijital.eksik.acikRizaTarihi)}
                </span>
              )}
            </div>
            {dijital.onay?.belgeVar && (
              <Button color="link" size="sm" className="p-0 text-start" disabled={mesgul} onClick={belgeyiAc}>
                {isleniyor === "belge" ? <Spinner size="sm" /> : <><FileDown size={14} className="me-1" />KVKK belgesi</>}
              </Button>
            )}
            {dijital.onay && dijital.bekleyen && (
              <span className="small text-muted">Yeni bağlantı gönderildi: {saatYaz(dijital.bekleyen.gonderimTarihi)}</span>
            )}
          </div>
          <div className="ms-auto d-flex flex-column align-items-end">
            <div className="d-flex gap-1 flex-wrap justify-content-end">
              {kvkkMockModu() && dijital.bekleyen && (
                <Button
                  color="light" size="sm" disabled={mesgul || salt} onClick={simuleEt}
                  title="Örnek: kişi bağlantıdan onaylamış gibi"
                >
                  {isleniyor === "simule" ? <Spinner size="sm" /> : "Onayı simüle et"}
                </Button>
              )}
              <Button
                color="primary" outline={dijital.gonderEtiketi === "Yeniden Gönder"} size="sm"
                disabled={mesgul || salt || dijital.telefonYok} onClick={gonder}
              >
                {isleniyor === "gonder" ? <Spinner size="sm" /> : <><Send size={14} className="me-1" />{dijital.gonderEtiketi}</>}
              </Button>
            </div>
            {dijital.telefonYok && <span className="small text-muted mt-1">Telefon numarası yok</span>}
            {dijital.onay?.sonGecerlilik && (
              <span className={`small mt-1 ${sonGecerlilikRengi(dijital.onay.suresiDoldu)}`}>
                Son geçerlilik: {gunYaz(dijital.onay.sonGecerlilik)}
              </span>
            )}
          </div>
        </div>

        {/* ── Islak İmzalı Belge ───────────────────────────────────────── */}
        <div className="d-flex align-items-start gap-2 flex-wrap">
          <span className="text-muted small pt-1" style={ETIKET}>Islak İmzalı Belge</span>
          <ButtonGroup size="sm">
            <Button
              color="light" className={`border ${islak.alindi ? "text-muted" : "fw-semibold text-body"}`}
              style={islak.alindi ? undefined : { background: "#e9ebec" }}
              disabled={mesgul || salt || !islak.alindi} onClick={() => onStatus?.(0)}
            >
              Yok
            </Button>
            <Button
              color={islak.alindi ? "green" : "light"} className={islak.alindi ? "fw-semibold" : "border text-muted"}
              disabled={mesgul || salt || islak.alindi} onClick={() => onStatus?.(3)}
            >
              Alındı
            </Button>
          </ButtonGroup>
          <div className="ms-auto d-flex flex-column align-items-end">
            <div className="d-flex gap-1 flex-wrap justify-content-end">
              {kvkkBelge?.fileId != null && (
                <Button color="light" size="sm" onClick={onIncele}>İncele</Button>
              )}
              {kvkkBelge?.fileId != null && kvkkBelge.status === 0 && !kvkkBelge.expired && (
                <Button color="green" size="sm" disabled={mesgul} onClick={onOnayla}>
                  {kvkkIsi === "onayla" ? <Spinner size="sm" /> : <><CheckCircle2 size={14} className="me-1" />Onayla</>}
                </Button>
              )}
              <Button color="primary" outline size="sm" disabled={mesgul || salt} onClick={dosyaSec}>
                {kvkkIsi === "yukle"
                  ? <Spinner size="sm" />
                  : <><Upload size={14} className="me-1" />{kvkkBelge?.fileId != null ? "Yeniden yükle" : "Yükle"}</>}
              </Button>
              <input
                id="kisi-kvkk-dosya" type="file" className="d-none" accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onYukle?.(file);
                  e.target.value = "";
                }}
              />
            </div>
            {(dosyaDurumu || islak.sonGecerlilik) && (
              <div className="d-flex align-items-center gap-2 mt-1">
                {dosyaDurumu && <Rozet hex={statusBackground(dosyaDurumu.color)}>{dosyaDurumu.label}</Rozet>}
                {islak.sonGecerlilik && (
                  <span className={`small ${sonGecerlilikRengi(islak.suresiDoldu)}`}>
                    Son geçerlilik: {gunYaz(islak.sonGecerlilik)}
                  </span>
                )}
              </div>
            )}
            {/* #503: kurumun KVKK İmza Belgeleri (56) için verdiği boş form -- ıslak imzalı asıl bu formla alınır. */}
            <BelgeSablonuBaglantilari sablon={sablon} className="small mt-1 text-end" />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
